import { useMemo, useState } from 'react';
import type {
  CircleActivity,
  CircleChallenge,
  CircleListItem,
  LeaderboardEntry,
  SharedMeal,
  WorkoutPing,
} from '../../types/circles';
import { PremiumCard } from '../premium';

const MAX_VISIBLE_ATTENDEES = 2;

type PingAttendee = { userId: string; name: string };

function resolvePingAttendees(
  ping: WorkoutPing,
  memberNames: Record<string, string>,
): { joining: PingAttendee[]; maybe: PingAttendee[] } {
  const responses = { ...ping.responses };
  if (!responses[ping.senderId]) {
    responses[ping.senderId] = 'join';
  }

  const nameFor = (userId: string) =>
    memberNames[userId] ?? (userId === ping.senderId ? ping.senderName : 'Member');

  const joining: PingAttendee[] = [];
  const maybe: PingAttendee[] = [];

  for (const [userId, response] of Object.entries(responses)) {
    if (response === 'cant') continue;
    const entry = { userId, name: nameFor(userId) };
    if (response === 'join') joining.push(entry);
    else if (response === 'maybe') maybe.push(entry);
  }

  joining.sort((a, b) => {
    if (a.userId === ping.senderId) return -1;
    if (b.userId === ping.senderId) return 1;
    return a.name.localeCompare(b.name);
  });
  maybe.sort((a, b) => a.name.localeCompare(b.name));

  return { joining, maybe };
}

function AttendeeChip({ name, tone }: { name: string; tone: 'join' | 'maybe' }) {
  return (
    <span
      className={`inline-block max-w-[5.5rem] truncate rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        tone === 'join' ? 'bg-emerald-500/25 text-emerald-200' : 'bg-amber-500/20 text-amber-200'
      }`}
      title={name}
    >
      {name}
    </span>
  );
}

function PingAttendeePanel({
  ping,
  memberNames,
}: {
  ping: WorkoutPing;
  memberNames: Record<string, string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const { joining, maybe } = useMemo(() => resolvePingAttendees(ping, memberNames), [ping, memberNames]);

  if (joining.length === 0 && maybe.length === 0) return null;

  const joinOverflow = Math.max(0, joining.length - MAX_VISIBLE_ATTENDEES);
  const visibleJoining = joining.slice(0, MAX_VISIBLE_ATTENDEES);
  const visibleMaybe = joinOverflow > 0 ? [] : maybe.slice(0, MAX_VISIBLE_ATTENDEES);
  const maybeOverflow =
    joinOverflow > 0 ? maybe.length : Math.max(0, maybe.length - MAX_VISIBLE_ATTENDEES);
  const totalOverflow = joinOverflow + maybeOverflow;

  if (expanded) {
    return (
      <div className="absolute right-0 top-0 z-10 w-[min(100%,11rem)] rounded-xl border border-white/10 bg-[#0f1628] p-2.5 shadow-lg">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">RSVPs</span>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[10px] font-semibold text-zinc-400 hover:text-zinc-200"
          >
            Close
          </button>
        </div>
        {joining.length > 0 ? (
          <div className="mb-2">
            <div className="text-[10px] font-semibold text-emerald-400/90">Joining</div>
            <div className="mt-1 flex flex-wrap justify-end gap-1">
              {joining.map((a) => (
                <AttendeeChip key={a.userId} name={a.name} tone="join" />
              ))}
            </div>
          </div>
        ) : null}
        {maybe.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold text-amber-400/90">Maybe</div>
            <div className="mt-1 flex flex-wrap justify-end gap-1">
              {maybe.map((a) => (
                <AttendeeChip key={a.userId} name={a.name} tone="maybe" />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex max-w-[48%] shrink-0 flex-col items-end gap-1">
      {visibleJoining.length > 0 ? (
        <div className="flex flex-wrap justify-end gap-1">
          {visibleJoining.map((a) => (
            <AttendeeChip key={a.userId} name={a.name} tone="join" />
          ))}
        </div>
      ) : null}
      {visibleMaybe.length > 0 ? (
        <div className="flex flex-wrap justify-end gap-1">
          {visibleMaybe.map((a) => (
            <AttendeeChip key={a.userId} name={a.name} tone="maybe" />
          ))}
        </div>
      ) : null}
      {totalOverflow > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[10px] font-semibold text-sky-300 hover:text-sky-200"
        >
          +{totalOverflow} more
        </button>
      ) : null}
    </div>
  );
}

export function CircleListCard({
  circle,
  onClick,
  delay = 0,
}: {
  circle: CircleListItem;
  onClick: () => void;
  delay?: number;
}) {
  return (
    <PremiumCard delay={delay} onClick={onClick} className="!p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-lg font-bold text-[var(--text-primary)]">{circle.name}</div>
          {circle.description ? (
            <p className="mt-1 line-clamp-2 text-sm text-[var(--text-muted)]">{circle.description}</p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-white/8 px-2.5 py-1 text-[var(--text-secondary)]">
              {circle.memberCount} members
            </span>
            <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 font-semibold text-emerald-300">
              {circle.weeklyProgressPct}% this week
            </span>
            {circle.todayActivityCount > 0 ? (
              <span className="rounded-full bg-sky-500/15 px-2.5 py-1 text-sky-200">
                {circle.todayActivityCount} today
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/30 to-sky-500/30 ring-1 ring-white/10">
          <span className="text-xl font-bold tabular-nums text-white">{circle.yourDailyScore}</span>
          <span className="text-[9px] uppercase tracking-wide text-white/70">You</span>
        </div>
      </div>
    </PremiumCard>
  );
}

export function LeaderboardRow({
  entry,
  delay = 0,
  onClick,
}: {
  entry: LeaderboardEntry;
  delay?: number;
  onClick?: () => void;
}) {
  const deltaLabel =
    entry.rankDelta > 0
      ? `+${entry.rankDelta} today`
      : entry.rankDelta < 0
        ? `${entry.rankDelta} today`
        : '—';

  return (
    <PremiumCard delay={delay} onClick={onClick} className={`!p-4 ${entry.isYou ? '!ring-1 !ring-sky-500/40' : ''} ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/8 text-sm font-bold text-[var(--text-muted)]">
          {entry.rank ?? '—'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold text-[var(--text-primary)]">
              {entry.name}
              {entry.isYou ? <span className="ml-1 text-xs text-sky-300">(You)</span> : null}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-[var(--text-muted)]">{entry.highlight}</div>
          {entry.streak != null && entry.streak > 0 ? (
            <div className="mt-1 text-[11px] font-medium text-amber-300">{entry.streak}-day streak</div>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-bold tabular-nums text-white">
            {entry.dailyScore ?? '—'}
          </div>
          <div
            className={`text-[11px] font-medium ${
              entry.rankDelta > 0 ? 'text-emerald-400' : entry.rankDelta < 0 ? 'text-amber-300' : 'text-zinc-500'
            }`}
          >
            {deltaLabel}
          </div>
        </div>
      </div>
    </PremiumCard>
  );
}

export function ActivityFeedItem({ item }: { item: CircleActivity }) {
  const time = new Date(item.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return (
    <div className="flex gap-3 rounded-2xl border border-white/6 bg-white/[0.03] px-4 py-3">
      <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-sky-400 to-violet-400" />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-[var(--text-primary)]">{item.message}</p>
        <p className="mt-1 text-[11px] text-[var(--text-muted)]">{time}</p>
      </div>
    </div>
  );
}

export function WorkoutPingCard({
  ping,
  currentUserId,
  memberNames = {},
  onRespond,
}: {
  ping: WorkoutPing;
  currentUserId?: string;
  memberNames?: Record<string, string>;
  onRespond?: (r: 'join' | 'maybe' | 'cant') => void;
}) {
  const when = ping.proposedTime
    ? new Date(ping.proposedTime).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    : null;
  const myResponse = currentUserId ? ping.responses[currentUserId] : undefined;

  return (
    <PremiumCard variant="train" className="relative !p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-300">Workout invite</div>
          <div className="mt-1 font-display text-base font-bold text-white">{ping.message || ping.workoutType}</div>
          <p className="mt-1 text-sm text-zinc-300">{ping.senderName}</p>
          {when ? <p className="mt-2 text-xs text-zinc-400">{when}</p> : null}
        </div>
        <PingAttendeePanel ping={ping} memberNames={memberNames} />
      </div>
      {onRespond ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {(['join', 'maybe', 'cant'] as const).map((r) => {
            const selected = myResponse === r;
            return (
              <button
                key={r}
                type="button"
                disabled={selected}
                onClick={() => onRespond(r)}
                className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition-all ${
                  r === 'join'
                    ? selected
                      ? 'bg-emerald-500/35 text-emerald-100 ring-2 ring-emerald-400/60'
                      : 'bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-500/30 hover:bg-emerald-500/30'
                    : r === 'maybe'
                      ? selected
                        ? 'bg-amber-500/30 text-amber-100 ring-2 ring-amber-400/55'
                        : 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/25 hover:bg-amber-500/25'
                      : selected
                        ? 'bg-white/15 text-zinc-100 ring-2 ring-white/25'
                        : 'bg-white/8 text-zinc-300 ring-1 ring-white/10 hover:bg-white/12'
                } ${selected ? 'cursor-default' : ''}`}
              >
                {selected ? '✓ ' : ''}
                {r === 'join' ? 'Join' : r === 'maybe' ? 'Maybe' : "Can't"}
              </button>
            );
          })}
        </div>
      ) : null}
    </PremiumCard>
  );
}

export function SharedMealCard({
  meal,
  onSave,
  onLog,
  onAiAdjust,
}: {
  meal: SharedMeal;
  onSave?: () => void;
  onLog?: () => void;
  onAiAdjust?: () => void;
}) {
  return (
    <PremiumCard variant="nutrition" className="!p-4">
      <div className="flex gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500/25 to-rose-500/20 text-2xl ring-1 ring-white/10">
          🍕
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold text-white">{meal.title}</div>
          <p className="mt-1 text-sm text-zinc-300">
            {meal.calories} cal · {Math.round(meal.proteinG)}g protein
          </p>
          <p className="text-xs text-zinc-500">
            {meal.prepTime ? `${meal.prepTime} min prep · ` : ''}
            {meal.rating != null ? `${meal.sharedBy} rated ${meal.rating}/10` : `Shared by ${meal.sharedBy}`}
          </p>
        </div>
      </div>
      {meal.recipe ? <p className="mt-3 text-xs leading-relaxed text-zinc-400">{meal.recipe}</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {onSave ? (
          <button type="button" onClick={onSave} className="rounded-xl bg-white/8 px-3 py-1.5 text-xs font-semibold text-zinc-200 ring-1 ring-white/10">
            Save recipe
          </button>
        ) : null}
        {onLog ? (
          <button type="button" onClick={onLog} className="rounded-xl bg-sky-500/20 px-3 py-1.5 text-xs font-semibold text-sky-200 ring-1 ring-sky-500/30">
            Log this meal
          </button>
        ) : null}
        {onAiAdjust ? (
          <button type="button" onClick={onAiAdjust} className="rounded-xl bg-violet-500/15 px-3 py-1.5 text-xs font-semibold text-violet-200 ring-1 ring-violet-500/25">
            Ask AI to adjust
          </button>
        ) : null}
      </div>
    </PremiumCard>
  );
}

export function ChallengeCard({
  challenge,
  memberCount,
  onJoin,
}: {
  challenge: CircleChallenge;
  memberCount: number;
  onJoin?: () => void;
}) {
  const maxProgress = Math.max(...Object.values(challenge.progress), 1);
  const target = challenge.goals.target ?? 5;
  const pct = Math.round((maxProgress / target) * 100);

  return (
    <PremiumCard variant="success" className="!p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-base font-bold text-white">{challenge.title}</div>
          <p className="mt-1 text-xs text-emerald-200/80">
            {challenge.participants.length} of {memberCount} in · {pct}% max progress
          </p>
        </div>
        {onJoin ? (
          <button
            type="button"
            onClick={onJoin}
            className="shrink-0 rounded-xl bg-emerald-500/25 px-3 py-1.5 text-xs font-semibold text-emerald-100 ring-1 ring-emerald-500/35"
          >
            Join
          </button>
        ) : null}
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-black/30">
        <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-sky-400" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </PremiumCard>
  );
}

export function QuickActionButton({
  label,
  emoji,
  onClick,
}: {
  label: string;
  emoji: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-press flex min-w-[5.5rem] flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3 text-center ring-1 ring-white/5"
    >
      <span className="text-xl">{emoji}</span>
      <span className="text-[11px] font-semibold leading-tight text-[var(--text-secondary)]">{label}</span>
    </button>
  );
}

export function CircleProgressRing({ pct, size = 88 }: { pct: number; size?: number }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={8} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="url(#circleGrad)"
          strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
        <defs>
          <linearGradient id="circleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold tabular-nums text-white">{pct}%</span>
        <span className="text-[9px] uppercase tracking-wide text-zinc-400">Today</span>
      </div>
    </div>
  );
}

export function CirclesSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-36 rounded-3xl bg-white/5" />
      <div className="h-28 rounded-2xl bg-white/5" />
      <div className="h-28 rounded-2xl bg-white/5" />
    </div>
  );
}
